import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import './Button.css';
import Spinner from '../Spinner';

const TYPES = ['default', 'primary', 'secondary', 'tertiary', 'ghost', 'icon'];
const COMPONENTS = ['button', 'span', 'div', 'a'];

const Button = ({
  id,
  'data-testid': dataTestId,
  'data-dd-action-name': dataDdActionName,
  style,
  onClick,
  onFocus,
  onBlur,
  className,
  type = 'default',
  fullWidth = false,
  icon,
  size = 'md',
  withLoading = false,
  htmlType = 'button',
  disabled = false,
  children,
  onMouseOver,
  whiteBorder = false,
  ariaLabel,
  ariaDescribedBy,
  component: Component = 'button',
}) => {
  const isNativeButton = Component === 'button';

  return (
    <Component
      id={id}
      data-testid={dataTestId || id}
      data-dd-action-name={dataDdActionName}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      role={!isNativeButton ? 'button' : undefined}
      tabIndex={!isNativeButton ? 0 : undefined}
      style={style}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseOver={onMouseOver}
      className={clsx(
        !icon && 'alf-button',
        className,
        type === 'primary' &&
          'text-white bg-legacy-primary hover:bg-legacy-darkPurple disabled:opacity-50 disabled:cursor-not-allowed',
        type === 'secondary' &&
          'text-legacy-primary transition-colors bg-white border border-legacy-primary hover:bg-legacy-gray50',
        type === 'tertiary' &&
          'text-gray-700 transition-colors bg-white border border-gray-300 hover:bg-gray-50',
        type === 'ghost' && 'text-legacy-primary hover:text-gray-900',
        type === 'icon' && 'text-legacy-primary hover:text-gray-900',
        whiteBorder && 'white-border',
        fullWidth && 'w-full',
        size,
        withLoading ? 'loading' : '',
      )}
      type={isNativeButton ? htmlType : undefined}
      disabled={isNativeButton ? disabled : undefined}
    >
      {icon}
      {withLoading ? <Spinner color={type} /> : <span>{children}</span>}
    </Component>
  );
};

Button.propTypes = {
  id: PropTypes.string,
  'data-dd-action-name': PropTypes.string,
  withLoading: PropTypes.bool,
  children: PropTypes.any,
  className: PropTypes.string,
  icon: PropTypes.any,
  fullWidth: PropTypes.bool,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onMouseOver: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  type: PropTypes.oneOf(TYPES),
  component: PropTypes.oneOf(COMPONENTS),
  disabled: PropTypes.bool,
  style: PropTypes.object,
  htmlType: PropTypes.string,
  size: PropTypes.string,
  whiteBorder: PropTypes.bool,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
};

export default Button;
